import { mapInfo, type MapId } from "@69ly/maps";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { SpotSide } from "../lib/my-spot.ts";
import { Icon } from "./Icon.tsx";

/** "A Site, Jungle and Top of Mid": names joined the way a sentence lists them. */
function listOf(names: string[], and: string): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} ${and} ${names[names.length - 1]}`;
}

/**
 * The "From my spot" chip (0.27.0): on, the stuffs shown are those thrown from my spots
 * (Team › Who plays where) and the callouts next to them. Its tip says which spots;
 * without a spot it cannot be turned on, and its tip (opened by a tap) says where to
 * set one.
 */
export function MySpotChip({
  map,
  side,
  names,
  on,
  onChange,
  glass,
}: {
  map: MapId;
  side: SpotSide;
  /** My spots' names on this side; none: the chip is off and says why. */
  names: string[];
  on: boolean;
  onChange: (on: boolean) => void;
  /** Over the map (Live): the glass look of the map's controls. */
  glass?: boolean;
}) {
  const { t } = useTranslation();
  const tipId = useId();
  const box = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState(false);
  const ok = names.length > 0;
  const mapName = mapInfo(map).name;
  const sideName = side === "both" ? "" : t(`live.sideShort.${side}`);

  useEffect(() => {
    if (!tip) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setTip(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setTip(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [tip]);

  return (
    <span className={`my-spot${tip ? " is-open" : ""}`} ref={box}>
      <button
        type="button"
        className={`chip my-spot-chip${glass ? " is-glass" : ""}`}
        aria-pressed={ok && on}
        aria-disabled={!ok}
        aria-describedby={tipId}
        onClick={() => (ok ? onChange(!on) : setTip(!tip))}
      >
        <Icon name="user" size={14} />
        {t("mySpot.chip")}
      </button>
      <span className="my-spot-tip" role="tooltip" id={tipId}>
        {ok ? (
          <>
            <b>{t("mySpot.tipTitle")}</b>
            <span className="muted small">{t(side === "both" ? "mySpot.tipBoth" : "mySpot.tip", { side: sideName, map: mapName, spots: listOf(names, t("mySpot.and")) })}</span>
          </>
        ) : (
          <>
            <b>{t(side === "both" ? "mySpot.noneTitleBoth" : "mySpot.noneTitle", { side: sideName, map: mapName })}</b>
            <span className="muted small">
              {t("mySpot.noneBefore")} <Link to={`/team/positions?map=${map}${side === "both" ? "" : `&side=${side}`}`}>{t("mySpot.noneLink")}</Link> {t("mySpot.noneAfter")}
            </span>
          </>
        )}
      </span>
    </span>
  );
}
